import { Bot, Lock } from "lucide-react";

import { getThreadFlagStrings } from "@/lib/thread-flags";
import { cn } from "@/lib/utils";

/**
 * 2-segment AI-handling control (design 02-thread-tasks): Bot answering | Locked.
 * "Bot answering" is active only while no pause flag is set and clicking it clears both
 * pause flags (via onClearAiHandling — one atomic flags:remove, not two racing calls), so
 * legacy staff_handles threads can still be released. Clicking an active flag segment
 * toggles that flag off.
 *
 * DISABLED (for now): the "Staff handles" segment is hidden — the flag is no longer
 * applied anywhere; assignment lives on tasks only.
 */
export function AiHandlingControl({ thread, onThreadFlagChange, onClearAiHandling, disabled = false }) {
  const tid = String(thread?.threadId || thread?.id || "").trim();
  const flags = getThreadFlagStrings(thread);
  const isLocked = flags.includes("locked");
  // DISABLED (for now): staff_handles is retired and no longer counts as paused.
  // The clear action still removes it (legacy-data hygiene), but it never lights a
  // segment or blocks "Bot answering".
  // const isStaffHandles = flags.includes("staff_handles");
  const botAnswering = !isLocked;

  const segments = [
    {
      key: "bot",
      label: "Bot answering",
      icon: Bot,
      active: botAnswering,
      activeClassName: "bg-teal-600 text-white shadow-sm dark:bg-teal-500",
      title: "The concierge handles routine intents on its own",
      onClick: () => {
        if (!tid || botAnswering) return;
        if (onClearAiHandling) {
          void onClearAiHandling(tid);
          return;
        }
        if (isLocked) void onThreadFlagChange(tid, "locked", false);
        // if (isStaffHandles) void onThreadFlagChange(tid, "staff_handles", false);
      },
    },
    {
      key: "locked",
      label: "Locked",
      icon: Lock,
      active: isLocked,
      activeClassName: "bg-violet-600 text-white shadow-sm dark:bg-violet-500",
      title: "AI paused — needs a human; auto-creates a Reply task",
      onClick: () => {
        if (!tid) return;
        void onThreadFlagChange(tid, "locked", !isLocked);
      },
    },
    // DISABLED (for now): the staff_handles segment — the flag is no longer applied.
    // {
    //   key: "staff",
    //   label: "Staff handles",
    //   icon: UserRoundCheck,
    //   active: isStaffHandles,
    //   activeClassName: "bg-emerald-600 text-white shadow-sm dark:bg-emerald-500",
    //   title: "A named person owns replies; the bot stays quiet",
    //   onClick: () => {
    //     if (!tid) return;
    //     void onThreadFlagChange(tid, "staff_handles", !isStaffHandles);
    //   },
    // },
  ];

  return (
    <div
      role="group"
      aria-label="AI handling"
      className="grid grid-cols-2 gap-1 rounded-lg border border-border bg-card p-1 shadow-sm"
    >
      {segments.map((segment) => {
        const Icon = segment.icon;
        return (
          <button
            key={segment.key}
            type="button"
            disabled={disabled}
            aria-pressed={segment.active}
            title={segment.title}
            onClick={segment.onClick}
            className={cn(
              "inline-flex items-center justify-center gap-1 rounded-md px-1 py-1.5 text-[11px] leading-none transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
              segment.active
                ? cn("font-semibold", segment.activeClassName)
                : "font-medium text-muted-foreground hover:bg-muted hover:text-foreground",
            )}
          >
            <Icon className="h-3 w-3 shrink-0" strokeWidth={2.5} aria-hidden />
            <span className="truncate">{segment.label}</span>
          </button>
        );
      })}
    </div>
  );
}
